import React, { useRef, useState, useEffect } from "react";
import { promoterApi } from "../../api/client";
// import sign from "../Promoter/sing.png";

import {
  FiDownload,
  FiPrinter,
  FiArrowLeft,
  FiArrowRight,
} from "react-icons/fi";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useAuth } from "../../context/AuthContext";
import { toApiAbsoluteUrl } from "../../api/client";

const IdCard = ({ promoter }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await promoterApi.getProfile();
        const profileData = response?.profile || response?.IdCard || response;
        setProfile(profileData);
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      }
    };

    fetchProfile();
  }, []);

  const defaultPromoter = {
    name: "_______",
    fatherName: "_______",
    email: "_______@email.com",
    id: "OIM:_______",
    phone: "_________",
    address: {
      street: "City",
      city: "State",
      state: "Country",
      pincode: "_______",
      country: "India",
    },
    photo: process.env.PUBLIC_URL , signature: "_________",
  };

  const data = promoter || profile || defaultPromoter;

  // Helper to resolve possible url shapes to an absolute URL usable in <img src>
  const resolveMaybeUrl = (val) => {
    if (!val) return null;
    if (typeof val === "string") {
      if (val.startsWith("http") || val.startsWith("data:")) return val;
      return toApiAbsoluteUrl(val);
    }
    if (Array.isArray(val) && val.length > 0) {
      const first = val[0];
      if (!first) return null;
      return resolveMaybeUrl(first.url || first);
    }
    if (typeof val === "object" && val !== null) {
      return resolveMaybeUrl(val.url || val.path || null);
    }
    return null;
  };

  const photoUrl =
    resolveMaybeUrl(data?.documents?.photoUpload?.url) ||
    resolveMaybeUrl(data?.photo) ||
    process.env.PUBLIC_URL + "/logo512.png";

  // Prefer signatures in this order: logged-in user's signature, channel partner (parent) signature,
  // promoter.creator signature, direct promoter.signature, then bundled fallback `sign`.
  const resolvedSignatureUrl =
    resolveMaybeUrl(user?.signature) ||
    resolveMaybeUrl(
      data?.channel_partner?.signature || data?.channel_partner
    ) ||
    resolveMaybeUrl(data?.creator?.signature || data?.creator) ||
    resolveMaybeUrl(data?.documents?.signature || data?.signature) || "_____";
  const signatureUrl = resolvedSignatureUrl;
  const issuedAt = data?.createdAt ? new Date(data.createdAt) : new Date();
  const expiryAt = new Date(issuedAt.getTime());
  expiryAt.setFullYear(issuedAt.getFullYear() + 1);
  const cardRef = useRef();
  const [showBack, setShowBack] = useState(false);

  const waitForImages = (element) =>
    new Promise((resolve) => {
      const images = element.querySelectorAll("img");
      let loadedCount = 0;
      if (images.length === 0) resolve(true);
      images.forEach((img) => {
        if (img.complete) {
          loadedCount++;
          if (loadedCount === images.length) resolve(true);
        } else {
          img.onload = () => {
            loadedCount++;
            if (loadedCount === images.length) resolve(true);
          };
          img.onerror = () => {
            loadedCount++;
            if (loadedCount === images.length) resolve(true);
          };
        }
      });
    });

  const downloadPDF = async () => {
    if (!cardRef.current) return;

    setShowBack(false);
    await new Promise((resolve) => setTimeout(resolve, 300));
    await waitForImages(cardRef.current);

    const canvasFront = await html2canvas(cardRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
    });

    setShowBack(true);
    await new Promise((resolve) => setTimeout(resolve, 300));
    await waitForImages(cardRef.current);

    const canvasBack = await html2canvas(cardRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
    });

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const imgDataFront = canvasFront.toDataURL("image/png");
    const imgDataBack = canvasBack.toDataURL("image/png");

    const cardWidth = 86;
    const cardHeight = 54;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const x = (pageWidth - cardWidth) / 2;
    const frontY = 30;
    const backY = frontY + cardHeight + 20;

    pdf.addImage(imgDataFront, "PNG", x, frontY, cardWidth, cardHeight);
    pdf.addImage(imgDataBack, "PNG", x, backY, cardWidth, cardHeight);

    pdf.save(`${data.name || "IDCard"}_IDCard.pdf`);
    setShowBack(false);
  };

  const printCard = async () => {
    if (!cardRef.current) return;

    setShowBack(false);
    await new Promise((resolve) => setTimeout(resolve, 100));
    await waitForImages(cardRef.current);

    const canvasFront = await html2canvas(cardRef.current, {
      scale: 3,
      useCORS: true,
      allowTaint: true,
    });

    setShowBack(true);
    await new Promise((resolve) => setTimeout(resolve, 100));
    await waitForImages(cardRef.current);

    const canvasBack = await html2canvas(cardRef.current, {
      scale: 3,
      useCORS: true,
      allowTaint: true,
    });

    const imgDataFront = canvasFront.toDataURL("image/png");
    const imgDataBack = canvasBack.toDataURL("image/png");

    const printWindow = window.open("", "_blank", "width=1000,height=1000");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Print ID Card - ${data.name || ""}</title>
         <style>
  @page {
    margin: 20 !important;
    size: auto;
  }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .print-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10mm; /* front aur back ke beech space */
    width: 86mm; /* same as ID card width */
    margin: 0 auto;
  }

  .card-side {
    width: 86mm;  /* fixed physical width */
    height: 54mm; /* fixed physical height */
    border-left: 2px solid black;
border-right: 2px solid black;

    margin: 0;
    padding: 0;
  }

  img {
    width: 86mm;
    height: 54mm;
    display: block;
    object-fit: cover;
  }

  .side-label {
    display: none !important;
  }
</style>


        </head>
        <body>
          <div class="print-container">
            <div class="side-label">Front Side</div>
            <div class="card-side"><img src="${imgDataFront}" style="width:100%;" /></div>
            <div class="side-label">Back Side</div>
            <div class="card-side"><img src="${imgDataBack}" style="width:100%;" /></div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => window.close(), 1000);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setShowBack(false);
  };

  const renderAddress = (addr) => {
    if (!addr) return "-";
    if (typeof addr === "object") return Object.values(addr).join(", ");
    return addr;
  };

  const formatPhoneNumber = (value) => {
    if (!value) return "-";
    const str = String(value).trim();
    return str.replace(/^\+?91[-\s]?/, "");
  };
  const [imgError, setImgError] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white flex flex-col items-center py-6 px-4">
      <div className="text-center mb-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Promoter ID Card
        </h1>
        <p className="text-gray-300 text-lg">
          Professional Identity Management
        </p>
      </div>

      <div className="mb-6">
        <button
          onClick={() => setShowBack(!showBack)}
          className="group bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-xl flex items-center gap-3 font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
        >
          {showBack ? (
            <>
              <FiArrowLeft className="w-5 h-5 group-hover:animate-pulse" />
              Show Front Side
            </>
          ) : (
            <>
              Show Back Side
              <FiArrowRight className="w-5 h-5 group-hover:animate-pulse" />
            </>
          )}
        </button>
      </div>

      <div
        ref={cardRef}
        className="relative w-[520px] h-[320px]  overflow-hidden shadow-2xl border-4 border-black"
      >
        {!showBack ? (
          <div className="absolute inset-0 p-1 bg-white text-black font-semibold ">
            <div className="absolute inset-0 ">
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-32 -translate-x-32"></div>
            </div>

            <div className="absolute inset-0 opacity-10">
              <div
                className="absolute inset-0 bg-repeat"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z'/%3E%3C/g%3E%3C/svg%3E")`,
                }}
              ></div>
            </div>

            <div className="relative z-10 h-full p-6 flex">
              <div className="flex flex-col items-center w-[160px] pr-4 border-r border-black">
                <div className="relative mb-4">
                  <div className="w-28 h-28 rounded-2xl overflow-hidden border-4 border-black shadow-2xl ring-2 ring-white/30 bg-white/10">
                    <img
                      src={photoUrl}
                      alt={data.name || ""}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                      loading="eager"
                      onError={(e) => {
                        const fallback =
                          process.env.PUBLIC_URL + "/logo512.png";
                        if (e.currentTarget.src !== fallback) {
                          e.currentTarget.src = fallback;
                        }
                      }}
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                <div className="text-start">
                  <p className="text-xs">Date of Issue</p>
                  <p className="text-sm font-semibol">
                    {issuedAt.toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                  <div className="">
                    <p className="text-xs">Valid Thru</p>
                    <p className="text-sm font-semibold">
                      {expiryAt.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>

                <div className="text-center pt-1">
                  <div className="flex flex-col">
                    <div className="w-24 h-10 bg-white rounded flex items-center justify-center overflow-hidden border border-black/10">
                      {!imgError ? (
                        <img
                          src="https://images.pexels.com/photos/2754973/pexels-photo-274973.jpeg?auto=compress&cs=tinysrgb&w=600"
                          alt="signature"
                          className="w-full h-full object-contain"
                          onError={() => setImgError(true)}
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          loading="eager"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <span className="text-sm font-serif pt-4">
                            ___________
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="text-xs mb-1">Authorized Signature</span>
                  </div>
                </div>
              </div>
              <div className="flex-1 pl-6 flex flex-col justify-around">
                <div className="text-center">
                  <h2 className="text-xl font-bold">Online Indian Market</h2>
                  <p className="">Authorized Promoter</p>
                </div>

                <div>
                  <div className="flex justify-between items-end">
                    <p className="">ID - {data.id || "-"}</p>
                  </div>
                  <h3 className="mb-1 ">Name - {data.name || "-"}</h3>
                  <div>
                    <div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
                        <span>
                          Phone -{" "}
                          {formatPhoneNumber(
                            data.phone ||
                              data.phoneNumber ||
                              (user && user.phoneNumber)
                          )}
                        </span>
                        <span>Email - {data.email || "-"}</span>
                      </div>
                      <p>Father's - {data.fatherName || "-"}</p>
                    </div>
                    <div>
                      <p>Address - {renderAddress(data.address)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-1 left-36 ">
              <p className="text-bold">*NO CASH PAYMENT ALLOWED*</p>
            </div>

            <div className="absolute top-2 right-4 flex items-center gap-1 text-x font-mono">
              OIM VALID
              <svg
                className="w-4 h-4 text-blue-800"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z" />
              </svg>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900">
            {/* Back side content can remain unchanged */}
            <div className="relative z-10 h-full p-2 px-6 flex flex-col bg-white text-black font-semibold">
              <div className="text-center mb-2">
                <h2 className="text-xl font-bold">Online Indian Market</h2>
                <p className="text-sm">Terms & Conditions</p>
              </div>
              <div className="flex-1">
                <div className="text-xs">
                  <p className="font-semibold">
                    This ID Card is issued subject to the following terms:
                  </p>
                  <div className="space-y-1">
                    <p>
                      <span className="font-semibold">1. Validity:</span> This
                      card is valid for 1 year from date of issue and must be
                      renewed annually.
                    </p>
                    <p>
                      <span className="font-semibold">2. Ownership:</span> This
                      card remains the property of Online Indian Market and must
                      be surrendered upon request.
                    </p>
                    <p>
                      <span className="font-semibold">3. Use:</span> This card
                      is for identification purposes only and does not confer
                      any authority to enter into contracts on behalf of OIM.
                    </p>
                  </div>
                  <div className="pt-1">
                    <p className="font-semibold">Emergency Contact:</p>
                    <p>
                      Phone:{user.phoneNumber || "-"} | Email:
                      onlineindianmarket@gmail.com
                      {/* {data.email || "-"} */}
                    </p>
                    <p className="text-xs mt-1">
                      Issued on:{" "}
                      {new Date(data.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}{" "}
                      | ID: {data.id || "-"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <p className="text-xs">
                  This card is machine readable and contains security features
                  to prevent duplication
                </p>
                <p className="text-sm">
                  *We do not encourage any type of illegal activities.*
                </p>
              </div>
            </div>
            <div className="absolute bottom-4 right-1 text-[10px] text-white font-mono rotate-90 origin-bottom-right">
              OFFICIAL DOCUMENT
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-6 mt-12">
        <button
          onClick={downloadPDF}
          className="group bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-2xl flex items-center gap-3 font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
        >
          <FiDownload className="w-5 h-5 group-hover:animate-bounce" /> Download
          PDF
        </button>
        <button
          onClick={printCard}
          className="group bg-gradient-to-r from-green-600 to-teal-600 px-8 py-4 rounded-2xl flex items-center gap-3 font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
        >
          <FiPrinter className="w-5 h-5 group-hover:animate-bounce" /> Print
          Card
        </button>
      </div>
    </div>
  );
};

export default IdCard;
